import React, { useState, useCallback, useMemo, useRef } from "react";
import ConfigurablePdfViewer from "components/PdfViewer/ConfigurablePdfViewer";
import { Card } from "react-bootstrap";
import { hasRoleIn } from "rbac/RbacHelper";
import PERMISSIONS from "rbac/Permissions.json";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Breadcrumb from "react-bootstrap/Breadcrumb";
import DiscardFax from "components/UnprocessedFaxDetails/DiscardFax";
import UnprocessedFaxLinkSection from "components/UnprocessedFaxDetails/UnprocessedFaxLinkSection";
import { UnprocessedFaxConstants } from "lookup/UnprocessedFaxConstants";
import { isStringEmptyOrNull } from "services/UtilityService";
import Select from "react-select";
import { useUnprocessedFaxDetails } from "./useUnprocessedFaxDetails";
import ConfirmationModal from "components/ConfirmationModal";
import { UNPROCESSED_FAX_PROGRAM_TYPE, getFaxDetailsUiRules } from "lookup/UnprocessedFaxListConstantsV2";
import withRouter from "utils/withRouter";
import { useNavigate } from "react-router-dom";

const getFaxTypeKey = (value = "") => {
  const normalized = value.toLowerCase();
  if (normalized === "unknown") return UNPROCESSED_FAX_PROGRAM_TYPE.UNKNOWN;
  if (normalized === "nonswitch" || normalized === "non-switch") return UNPROCESSED_FAX_PROGRAM_TYPE.NON_SWITCH;
  return UNPROCESSED_FAX_PROGRAM_TYPE.SWITCH;
};

const UnprocessedFaxDetails = (props) => {
  const { faxInfoId, faxType } = props.match.params;
  const listQueryParams = props.location?.search || "";
  const queryParams = new URLSearchParams(listQueryParams);
  const faxProgramCode = queryParams.get("faxProgramCode") || "";
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingSubmit, setPendingSubmit] = useState(null);
  const [hiddenPages, setHiddenPages] = useState([]);
  const [resetPrescriberSelection, setResetPrescriberSelection] = useState(0);
  const [resetSwitchSelection, setResetSwitchSelection] = useState(0);
  const [resetCustomListSelection, setResetCustomListSelection] = useState(0);
  const [bypassAutoDisposition, setBypassAutoDisposition] = useState(false);
  const formikRef = useRef(null);
  const linkSectionRef = useRef(null);
  const normalizedFaxType = useMemo(() => getFaxTypeKey(faxType), [faxType]);
  const faxTypeConfig = useMemo(
    () => getFaxDetailsUiRules(normalizedFaxType, faxProgramCode),
    [normalizedFaxType, faxProgramCode]
  );
  const linkFieldsConfig = faxTypeConfig.linkFieldsConfig || [];
  const navigate = useNavigate();
  // Use custom hook for all state and operations
  const {
    loading,
    error,
    unprocessedFaxDetails,
    faxPriority,
    note,
    noteObject,
    allAvailableTags,
    allSelectedTags,
    selectedTags,
    tagsChanged,
    saveTagsLoading,
    saveNoteLoading,
    updateNoteLoading,
    deleteNoteLoading,
    linkUnprocessedFaxToSwitch: linkFaxToSwitch,
    linkAndArchiveFax,
    submitConfiguredLinkFax,
    linkAndArchiveLoading,
    discardFax,
    completePageLinking,
    linkedPages: hookLinkedPages,
    setPriorityOfUnprocessedFax,
    saveNote,
    updateNote,
    deleteNote,
    saveTags,
    handleTagsChange,
    setNote,
    getFaxListRedirectPath,
  } = useUnprocessedFaxDetails(faxInfoId, faxType, faxProgramCode, listQueryParams);

  // Merge hookLinkedPages (fax-archive backed) with locally hidden pages for NON_SWITCH + SWITCH
  const allHiddenPages = useMemo(() => {
    if (
      (faxTypeConfig.key === UNPROCESSED_FAX_PROGRAM_TYPE.NON_SWITCH ||
        faxTypeConfig.key === UNPROCESSED_FAX_PROGRAM_TYPE.SWITCH) &&
      hookLinkedPages.length > 0
    ) {
      const combined = new Set([...hiddenPages, ...hookLinkedPages]);
      return Array.from(combined).sort((a, b) => a - b);
    }
    return hiddenPages;
  }, [hiddenPages, hookLinkedPages, faxTypeConfig.key]);

  // Check if any pages are linked for NON_SWITCH
  const hasLinkedPages = useMemo(() => {
    // NON_SWITCH uses hookLinkedPages; SWITCH uses local hiddenPages populated after linking requests.
    return hookLinkedPages.length > 0 || hiddenPages.length > 0;
  }, [hookLinkedPages, hiddenPages]);

  // Handle linking fax to switch
  const handleLinkUnprocessedFaxToSwitch = useCallback(
    async (values, setSubmitting) => {
      // For NON_SWITCH and SWITCH, show confirmation modal first (match NON_SWITCH UX)
      if (
        faxTypeConfig.key === UNPROCESSED_FAX_PROGRAM_TYPE.NON_SWITCH ||
        faxTypeConfig.key === UNPROCESSED_FAX_PROGRAM_TYPE.SWITCH
      ) {
        setPendingSubmit({ values, setSubmitting });
        setShowConfirmModal(true);
        return;
      }

      // For other types, proceed normally
      setSubmitting(true);
      try {
        const { faxId, linkPages } = values;
        await linkFaxToSwitch(faxId, linkPages, false, faxProgramCode, bypassAutoDisposition);
      } finally {
        setSubmitting(false);
      }
    },
    [linkFaxToSwitch, faxTypeConfig.key, faxProgramCode, bypassAutoDisposition]
  );

  // Helper function to parse page numbers from comma-separated string
  const parsePageNumbers = useCallback((pageString) => {
    if (!pageString || typeof pageString !== 'string') return [];
    return pageString
      .split(',')
      .map(page => parseInt(page.trim(), 10))
      .filter(page => !isNaN(page) && page > 0);
  }, []);

  // Helper function to check if all fax pages have been linked
  const checkIfAllPagesLinked = useCallback(
    (linkPages = [], hiddenPages = [], totalPages) => {
      if (!Number.isInteger(totalPages) || totalPages <= 0) return false;

      // Collect already linked pages
      const linkedPages = new Set(hiddenPages);

      // Collect pages from the current linking request
      linkPages.forEach(({ referenceField, additionalField }) => {
        // For SWITCH: referenceField is the primary page index; treat it as linked too.
        const refPage = parseInt(String(referenceField), 10);
        if (!isNaN(refPage) && refPage > 0) linkedPages.add(refPage);
        if (!additionalField) return;

        parsePageNumbers(String(additionalField))
          .forEach(page => linkedPages.add(page));
      });

      // Verify all pages are linked
      return Array.from({ length: totalPages }, (_, i) =>
        linkedPages.has(i + 1)
      ).every(Boolean);
    },
    [parsePageNumbers]
  );

  // Handle confirmation modal confirm
  const handleConfirmLinkAndArchive = useCallback(async () => {
    if (!pendingSubmit) return;

    setShowConfirmModal(false);
    const { values, setSubmitting } = pendingSubmit;
    setPendingSubmit(false);

    setSubmitting(true);
    const { faxId, linkPages } = values;

    // Check if all pages are linked
    const isFaxCompletelyLinked = checkIfAllPagesLinked(
      linkPages,
      allHiddenPages,
      unprocessedFaxDetails?.noOfPages
    );

    try {
      const resolvedFaxProgram =
        faxTypeConfig.linkSubmitApi?.faxProgram ?? faxProgramCode;

      if (faxTypeConfig.linkSubmitApi) {
        await submitConfiguredLinkFax(
          faxId,
          linkPages,
          isFaxCompletelyLinked,
          linkFieldsConfig,
          {
            ...faxTypeConfig.linkSubmitApi,
            faxProgram: resolvedFaxProgram,
          },
          bypassAutoDisposition
        );
      } else if (faxTypeConfig.key === UNPROCESSED_FAX_PROGRAM_TYPE.NON_SWITCH) {
        await linkAndArchiveFax(
          faxId,
          linkPages,
          isFaxCompletelyLinked,
          linkFieldsConfig,
          faxProgramCode,
          bypassAutoDisposition
        );
      } else if (faxTypeConfig.key === UNPROCESSED_FAX_PROGRAM_TYPE.SWITCH) {
        await linkFaxToSwitch(
          faxId,
          linkPages,
          isFaxCompletelyLinked,
          faxProgramCode,
          bypassAutoDisposition
        );
      } else {
        await linkFaxToSwitch(
          faxId,
          linkPages,
          isFaxCompletelyLinked,
          faxProgramCode,
          bypassAutoDisposition
        );
      }

      // Extract and add linked pages to hidden pages (NON_SWITCH + SWITCH)
      if (
        faxTypeConfig.key === UNPROCESSED_FAX_PROGRAM_TYPE.NON_SWITCH ||
        faxTypeConfig.key === UNPROCESSED_FAX_PROGRAM_TYPE.SWITCH
      ) {
        setHiddenPages((prevHiddenPages) => {
          const pagesToHide = new Set(prevHiddenPages);
          const pageLinkSourceFields =
            faxTypeConfig.linkSubmitApi?.pageLinkSourceFields;

          linkPages.forEach((linkPage) => {
            if (pageLinkSourceFields?.length) {
              pageLinkSourceFields.forEach((fieldName) => {
                if (!linkPage[fieldName]) return;
                parsePageNumbers(String(linkPage[fieldName])).forEach((page) =>
                  pagesToHide.add(page)
                );
              });
              return;
            }

            const refPage = parseInt(String(linkPage.referenceField), 10);
            if (!isNaN(refPage) && refPage > 0) pagesToHide.add(refPage);
            if (linkPage.additionalField) {
              const parsedPages = parsePageNumbers(String(linkPage.additionalField));
              parsedPages.forEach((page) => pagesToHide.add(page));
            }
          });
          return Array.from(pagesToHide).sort((a, b) => a - b);
        });
      }

      linkSectionRef.current?.resetLinkForm();
      setResetPrescriberSelection((prev) => prev + 1);
      setResetSwitchSelection((prev) => prev + 1);
      setResetCustomListSelection((prev) => prev + 1);
    } catch (error) {} finally {
      setSubmitting(false);
    }
  }, [
    pendingSubmit,
    linkAndArchiveFax,
    submitConfiguredLinkFax,
    linkFaxToSwitch,
    faxTypeConfig,
    faxProgramCode,
    linkFieldsConfig,
    parsePageNumbers,
    checkIfAllPagesLinked,
    allHiddenPages,
    unprocessedFaxDetails?.noOfPages,
    bypassAutoDisposition,
  ]);

  // Handle confirmation modal cancel
  const handleCancelLinkAndArchive = useCallback(() => {
    setShowConfirmModal(false);
    setPendingSubmit(false);
  }, []);

  // Handle save or update note
  const handleSaveOrUpdateNote = useCallback(() => {
    if (noteObject?.uflNoteUuid?.length > 0) {
      updateNote();
    } else {
      saveNote();
    }
  }, [noteObject, updateNote, saveNote]);

  // Handle priority change
  const handlePriorityChange = useCallback(
    (e) => {
      const priority = e.target.value;
      setPriorityOfUnprocessedFax(priority);
    },
    [setPriorityOfUnprocessedFax]
  );

  const handleSaveTagsAction = useCallback(async () => {
    await saveTags();
    if (faxTypeConfig.navigateBackAfterTagSave) {
      navigate(getFaxListRedirectPath());
    }
  }, [saveTags, faxTypeConfig.navigateBackAfterTagSave]);

  // Loading and error states
  if (loading) {
    return "Fetching Fax Details ... ";
  }

  if (error || !unprocessedFaxDetails) {
    return error || "Failed to fetch Fax details";
  }

    return (
      <Container fluid>
        <Row>
          <Col md={5}>
            <Breadcrumb>
              <Breadcrumb.Item
                href={getFaxListRedirectPath()}
                linkProps={{
                  "data-testid": "unprocessed-fax-list-breadcrumb-link",
                }}
              >
                Unprocessed Fax List
              </Breadcrumb.Item>
              <Breadcrumb.Item active>Fax {faxInfoId}</Breadcrumb.Item>
            </Breadcrumb>
            {faxTypeConfig.showPriority && (
              <Row className="tiny-top-spacer">
                <Col
                  id="dispositionForContainer"
                  md={12}
                  style={{ fontSize: "1.5em" }}
                >
                  <span className="text-muted">{faxTypeConfig.headingText}</span>
                </Col>
                <Col md={12} className="tiny-top-spacer">
                  <Form.Group>
                    <Form.Label>Fax Priority</Form.Label>
                    <Form.Select
                      data-testid="unprocessed-fax-priority-select"
                      value={faxPriority}
                      onChange={handlePriorityChange}
                    >
                      <option value="">None</option>
                      {Object.entries(
                        UnprocessedFaxConstants.UNPROCESSED_FAX_PRIORITY
                      ).map(([key, value]) => (
                        <option key={key} value={key}>
                          {value}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
            )}
            {faxTypeConfig.showAssociatedSwitch &&
            !isStringEmptyOrNull(unprocessedFaxDetails?.associatedSwitch) ? (
              <Row className="tiny-top-spacer">
                <Col md={12}>
                  <span className="text-muted">
                    Currently Associated With:{" "}
                    {unprocessedFaxDetails?.associatedSwitch}
                  </span>
                </Col>
              </Row>
            ) : null}
            {faxTypeConfig.showTags && (
              <>
                <hr />
                <Row className="tiny-top-spacer align-items-end">
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label>Tags</Form.Label>
                      <Select
                        isMulti
                        options={allAvailableTags.map(tag => ({ value: tag, label: tag }))}
                        value={selectedTags && selectedTags.length > 0
                          ? selectedTags.map(tag => ({ value: tag, label: tag }))
                          : []}
                        onChange={(selectedOptions) => {
                          const selectedValues = selectedOptions ? selectedOptions.map(opt => opt.value) : [];
                          handleTagsChange(selectedValues);
                        }}
                        placeholder="Select or remove tags"
                        isClearable
                      />
                    </Form.Group>
                  </Col>
                  <Col md={12} className="d-flex justify-content-end">
                    <Button
                      variant="primary"
                      onClick={handleSaveTagsAction}
                      disabled={!tagsChanged || saveTagsLoading}
                    >
                      {saveTagsLoading
                        ? "Saving..."
                        : faxTypeConfig.tagsButtonLabel}
                    </Button>
                  </Col>
                </Row>
              </>
            )}
            {faxTypeConfig.showBypassAutoDisposition && (
              <>
                <hr />
                <Row className="tiny-top-spacer align-items-center border rounded mx-0 px-3 py-2 g-0 bypass-auto-disposition-row">
                  <Col xs={10} className="d-flex align-items-center">
                    <Form.Label
                      htmlFor="bypass-auto-disposition-toggle"
                      className="mb-0 fw-bold"
                    >
                      {faxTypeConfig.bypassAutoDispositionLabel ||
                        "Bypass Auto Disposition"}
                    </Form.Label>
                  </Col>
                  <Col xs={2} className="d-flex align-items-center justify-content-end">
                    <Form.Check
                      type="switch"
                      id="bypass-auto-disposition-toggle"
                      data-testid="bypass-auto-disposition-toggle"
                      checked={bypassAutoDisposition}
                      onChange={(e) => setBypassAutoDisposition(e.target.checked)}
                      className="rm-form-switch bypass-auto-disposition-switch mb-0"
                    />
                  </Col>
                </Row>
              </>
            )}
            {faxTypeConfig.showDiscard && !faxTypeConfig.showLinkSection && (
              <>
                <hr />
                <Row>
                  <Col className="d-grid gap-2">
                    <DiscardFax
                      disallowEditing={false}
                      discardFax={discardFax}
                      faxId={faxInfoId}
                    />
                  </Col>
                </Row>
              </>
            )}
            {faxTypeConfig.showLinkSection && (
              <UnprocessedFaxLinkSection
                ref={linkSectionRef}
                faxInfoId={faxInfoId}
                faxTypeConfig={faxTypeConfig}
                linkFieldsConfig={linkFieldsConfig}
                noOfPages={unprocessedFaxDetails.noOfPages}
                formikRef={formikRef}
                onSubmit={handleLinkUnprocessedFaxToSwitch}
                linkAndArchiveLoading={linkAndArchiveLoading}
                hasLinkedPages={hasLinkedPages}
                discardFax={discardFax}
                completePageLinking={completePageLinking}
                resetPrescriberSelection={resetPrescriberSelection}
                resetSwitchSelection={resetSwitchSelection}
                resetCustomListSelection={resetCustomListSelection}
                onCustomListReset={() => setResetCustomListSelection((prev) => prev + 1)}
                note={note}
                noteObject={noteObject}
                setNote={setNote}
                onSaveOrUpdateNote={handleSaveOrUpdateNote}
                saveNoteLoading={saveNoteLoading}
                updateNoteLoading={updateNoteLoading}
                deleteNoteLoading={deleteNoteLoading}
                onDeleteNote={deleteNote}
              />
            )}
          </Col>

          <Col md={7}>
          {!hasRoleIn([PERMISSIONS.PERM_PHI_ACCESS]) ? (
              <Container className="list-container" fluid>
                <Card variant="primary" className={"mb-3"}>
                  <Card.Title className="text-center"><h5>User does not have PHI access to view PDF</h5></Card.Title>
                  </Card>
              </Container>
            ) : (
              <ConfigurablePdfViewer
                height="85vh"
                width="100%"
                url={`/pdf/prescriber-inbound-fax/${faxInfoId}`}
                hiddenPages={allHiddenPages}
                showPageNumber={true}
              />
            )}
          </Col>
        </Row>
        <ConfirmationModal
          show={showConfirmModal}
          onHide={handleCancelLinkAndArchive}
          onConfirm={handleConfirmLinkAndArchive}
          title={
            bypassAutoDisposition &&
            faxTypeConfig.linkConfirmModal.titleWhenBypass
              ? faxTypeConfig.linkConfirmModal.titleWhenBypass
              : faxTypeConfig.linkConfirmModal.title
          }
          body={
            bypassAutoDisposition &&
            faxTypeConfig.linkConfirmModal.bodyWhenBypass
              ? faxTypeConfig.linkConfirmModal.bodyWhenBypass
              : faxTypeConfig.linkConfirmModal.body
          }
          confirmLabel="Yes"
          cancelLabel="No"
          cancelVariant="light"
        />
      </Container>
    );
};

export default withRouter(UnprocessedFaxDetails);
